import React, { createContext, ReactNode, useContext, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { useProject } from './UseProject'
import { useTeams } from './UseTeam'
import useTranslation from './UseTranslation'

import {
  getExcelFirstSheetData,
  getExcelSecondSheetData,
} from '@/Helpers/NewDashboardHelper'
import { getNonWorkingDays } from '@/Helpers/ProgrammationHelper'
import { maxDate } from '@/Helpers/DateFunctions'

interface ProjectionValue {
  estimatedEndDate: string
  projectedEndDate: string
  weeks: any[]
  performance: number
  deviation: number
  actualPerformance: number
  projectProgress: any
  loaded: boolean
  extendedNonWorkingDays: string[]
}

const ProjectionContext = createContext<ProjectionValue | undefined>(undefined)

const DDMMYYYY_TO_YYYYMMDD = /^(\d{2})\/(\d{2})\/(\d{4})$/

const toISODate = (value: string): string => {
  if (!value) return ''
  const match = value.match(DDMMYYYY_TO_YYYYMMDD)
  if (!match) return ''
  const [, dd, mm, yyyy] = match
  return `${yyyy}-${mm}-${dd}`
}

export const ProjectionProvider = ({ children }: { children: ReactNode }) => {
  const { project, projectLoaded, nonWorkingDays } = useProject()
  const { teams } = useTeams()
  const { t } = useTranslation()

  const tasks = useSelector((state: any) => state.ProgrammationReducer?.tasks)
  const list = useSelector((state: any) => state.ProgrammationReducer?.list)
  const users = useSelector((state: any) => state.ProgrammationReducer?.users)
  const colorCodes = useSelector((state: any) => state.ProgrammationReducer?.colorCodes)
  const relations = useSelector((state: any) => state.ProgrammationReducer?.relations)
  const documents = useSelector((state: any) => state.ProgrammationReducer?.documents)

  const value = useMemo<ProjectionValue>(() => {
    const empty: ProjectionValue = {
      estimatedEndDate: '',
      projectedEndDate: '',
      weeks: [],
      performance: 0,
      deviation: 0,
      actualPerformance: 0,
      projectProgress: null,
      loaded: false,
      extendedNonWorkingDays: nonWorkingDays || [],
    }

    if (!projectLoaded || !project?._id) return empty

    const extendedEnd = maxDate(project.endsAt, project.realEnd) || project.endsAt
    const extendedNonWorkingDays = (project.startsAt && extendedEnd)
      ? getNonWorkingDays(project.startsAt, extendedEnd, project)
      : (nonWorkingDays || [])

    if (!Array.isArray(tasks) || !Array.isArray(list) || !Array.isArray(users)) {
      return { ...empty, extendedNonWorkingDays }
    }

    try {
      const firstSheet = getExcelFirstSheetData(
        tasks,
        list,
        users,
        colorCodes || [],
        extendedNonWorkingDays,
        teams || [],
        {},
        t
      )

      const {
        weeks: secondSheetWeeks = [],
        progress,
        estimatedEndDate = '',
        performance = 0,
        deviation = 0,
        actualPerformance = 0,
      } = getExcelSecondSheetData(project, firstSheet, extendedNonWorkingDays, t)

      const projectedEndDate = toISODate(estimatedEndDate)
      const projectedEnd = maxDate(project.endsAt, projectedEndDate) || project.endsAt

      const finalNonWorkingDays = (project.startsAt && projectedEnd && projectedEnd !== extendedEnd)
        ? getNonWorkingDays(project.startsAt, projectedEnd, project)
        : extendedNonWorkingDays

      return {
        estimatedEndDate,
        projectedEndDate,
        weeks: secondSheetWeeks,
        performance,
        deviation,
        actualPerformance,
        projectProgress: progress ?? null,
        loaded: true,
        extendedNonWorkingDays: finalNonWorkingDays,
      }
    } catch (err) {
      console.warn('[ProjectionProvider] failed to compute projection', err)
      return { ...empty, extendedNonWorkingDays }
    }
  }, [
    projectLoaded,
    project?._id,
    project?.startsAt,
    project?.endsAt,
    project?.realEnd,
    project?.nonWorkingDays,
    project?.settings,
    tasks,
    list,
    users,
    colorCodes,
    relations,
    documents,
    nonWorkingDays,
    teams,
    t,
  ])

  return (
    <ProjectionContext.Provider value={value}>
      {children}
    </ProjectionContext.Provider>
  )
}

export const useProjection = (): ProjectionValue => {
  const context = useContext(ProjectionContext)
  if (context === undefined) {
    throw new Error('useProjection must be used within a ProjectionProvider')
  }
  return context
}